import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import {
  RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid,
  ComparisonTable, ProsCons, RichFAQ, RelatedLinks, RichCTA, ChargerCard,
} from '../RichBlocks';
import { ENKEL_PRODUCTS } from '@/components/seo/content/ContentRegistry';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

export default function V2GGuide({ page }) {
  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = [
    { question: 'Hvad er V2G helt konkret?', answer: 'V2G (Vehicle-to-Grid) betyder, at din elbil kan sende strøm tilbage til elnettet. Du lader op, når strømmen er billig, og sender strøm retur, når den er dyr. Det kræver en V2G-kompatibel bil, en tovejsladeboks og en aftale med dit elselskab. Bilen fungerer reelt som et stort batteri på 60–100 kWh, der står stille i indkørslen.' },
    { question: 'Hvad er forskellen på V2G, V2H og V2L?', answer: 'V2L (Vehicle-to-Load) sender strøm til apparater – som en stor powerbank, og findes på mange biler i dag. V2H (Vehicle-to-Home) forsyner hele dit hus, fx ved strømafbrydelse. V2G (Vehicle-to-Grid) sender strøm tilbage til hele elnettet, så du kan tjene på prisforskelle. V2L er tilgængeligt nu; V2G ventes bredt udrullet i Danmark i 2026–2027.' },
    { question: 'Hvilke biler understøtter V2G i 2026?', answer: 'Kia EV9 er blandt de første CCS-biler med fuld V2G. Kia EV6 og Hyundai Ioniq 5/6 understøtter V2L/V2H med software på vej. Cupra Born og Nissan Leaf understøtter V2G via særligt udstyr. VW ID-modeller og Ford Capri/Explorer er planlagt til 2026. Tesla Model 3/Y understøtter endnu ikke V2G.' },
    { question: 'Hvad kan man realistisk tjene på V2G?', answer: 'Svenske pilotprojekter viser 5.000–15.000 kr. i besparelse om året under gunstige forhold. Et mere konservativt dansk estimat er 3.000–8.000 kr./år. Det afhænger kraftigt af prisforskellen mellem nat og spidsbelastning og af, hvor mange dage bilen er tilsluttet. Der findes endnu ingen officielle danske V2G-aftaler, så tallene er estimater.' },
    { question: 'Slider V2G mit batteri?', answer: 'Tovejsopladning slider marginalt mere end almindelig opladning. V2G-godkendte biler er designet til at håndtere det, og softwaren begrænser typisk brugen til 20–80% af batteriet. Studier af Nissan Leaf-ejere viser kun marginalt ekstra slid over fem år, og besparelsen overstiger typisk omkostningen.' },
    { question: 'Hvornår kan danskere bruge V2G?', answer: 'De juridiske rammer er ved at falde på plads i Energistyrelsen. Forventningen er, at private danskere kan indgå V2G-aftaler med elselskaber i 2026–2027. Sverige og Tyskland er 1–2 år foran. Køber du en V2G-forberedt ladeboks nu, er du klar den dag lovgivningen er på plads.' },
    { question: 'Hvilken ladeboks skal jeg vælge til V2G?', answer: 'Til private hjem er Zaptec Go 2 den billigste AC-baserede V2G-klare løsning. Den er allerede forberedt til tovejsopladning. Hos enkel energi koster Zaptec Go 2 5.999 kr. inkl. Saving Mode og serviceaftale. NexBlue Edge 2 er V2X-ready og et billigere alternativ.' },
  ];

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'forskel', label: 'V2G vs V2H vs V2L' },
    { id: 'biler', label: 'Hvilke biler' },
    { id: 'ladebokse', label: 'V2G-ladebokse' },
    { id: 'okonomi', label: 'Økonomi' },
    { id: 'krav', label: 'Krav' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'V2G-guide', path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline="V2G – sælg strøm tilbage til nettet 2026" description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow="Guide · Vehicle-to-Grid 2026"
        h1="V2G: Sælg strøm tilbage til nettet i 2026"
        intro="Din elbil kan blive et batteri, der tjener penge, mens den holder stille. Vi forklarer hvad V2G er, hvilke biler og ladebokse der understøtter det, hvad du realistisk kan tjene – og hvornår det kommer til Danmark."
        badges={['Opdateret 2026', 'Zaptec Go 2 er V2G-klar', 'Uafhængigt testet 5/5']}
        updated={updated}
        image="https://images.unsplash.com/photo-1593941707882-a5bba14938c7?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: '3.000–8.000 kr.', label: 'realistisk dansk estimat/år' },
          { value: '60–100 kWh', label: 'typisk batteri = 3–5 dages forbrug' },
          { value: '2026–2027', label: 'forventet dansk udrulning' },
          { value: 'Zaptec Go 2', label: 'billigste V2G-klare boks' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="Hvad er V2G – og er det relevant for dig nu?">
        <AnswerBox question="Hvad er V2G?">
          V2G (Vehicle-to-Grid) betyder, at din elbil <strong>sender strøm tilbage til elnettet</strong> i stedet for kun at modtage den.
          Du lader billigt om natten og sender strøm retur i de dyre aftentimer – og tjener på prisforskellen. Det kræver en V2G-kompatibel bil,
          en tovejsladeboks (fx Zaptec Go 2) og en V2G-aftale med dit elselskab. I Danmark ventes brede aftaler i <strong>2026–2027</strong>.
        </AnswerBox>
        <p>
          Forestil dig, at du lader bilen kl. 03 om natten til en lav spotpris. Kl. 18 om eftermiddagen, hvor strømmen kan koste mange gange mere,
          sender bilen strøm tilbage. Du tjener på forskellen, lader op igen til natten og gentager. Det lyder som fremtidsmusik – men det sker
          allerede i Sverige, 45 minutter fra den danske grænse, hvor Europas største V2G-projekt kører med 200 tovejsladere.
        </p>
      </ProseSection>

      <ProseSection id="forskel" heading="V2G, V2H og V2L – kend forskellen" alt>
        <p>De tre begreber forvirrer mange, men er enkle at holde adskilt:</p>
        <div className="my-6">
          <ComparisonTable
            caption="Forskel på V2L, V2H og V2G"
            columns={[{ name: 'Hvad gør den?' }, { name: 'Status i DK 2026', highlight: true }]}
            rows={[
              { label: 'V2L (to-Load)', values: ['Strøm til apparater – som en powerbank', 'Tilgængeligt nu (Kia EV6, Ioniq 5/6)'] },
              { label: 'V2H (to-Home)', values: ['Forsyner hele huset', 'Tilgængeligt (Kia EV9, Nissan Leaf)'] },
              { label: 'V2G (to-Grid)', values: ['Sender strøm til hele elnettet – du kan tjene penge', 'Pilotprojekter – bred udrulning 2026–2027'] },
            ]}
          />
        </div>
        <p>V2L kræver ingen særlig aftale. V2H og V2G kræver specifik bilhardware og en kompatibel ladeboks – og V2G kræver desuden en aftale med et elselskab.</p>
      </ProseSection>

      <ProseSection id="biler" heading="Hvilke biler understøtter V2G i 2026?">
        <p>Vigtig pointe: Ikke alle elbiler kan sende strøm retur – det kræver specifik hardware i selve bilen. Her er status for de mest relevante modeller på det danske marked:</p>
        <div className="my-6">
          <ComparisonTable
            caption="V2G-status pr. bilmodel 2026"
            columns={[{ name: 'V2G / V2H / V2L', highlight: true }, { name: 'Bemærkning' }]}
            rows={[
              { label: 'Kia EV9', values: ['✅ V2G-klar', 'En af de første CCS-biler med fuld V2G'] },
              { label: 'Kia EV6 / Ioniq 5 + 6', values: ['✅ V2L + V2H', 'V2H med adapter – V2G-software ventes'] },
              { label: 'Cupra Born', values: ['✅ V2G (Wallbox Quasar 2)', 'Første CCS-bil bekræftet V2G i EU'] },
              { label: 'Nissan Leaf', values: ['✅ V2G (CHAdeMO)', 'Mest moden – men ældre stiktype'] },
              { label: 'VW ID.4 / ID.7', values: ['⏳ Planlagt 2026', 'VW annoncerede V2G til alle MEB-biler'] },
              { label: 'Tesla Model 3 / Y', values: ['❌ Ikke endnu', 'Tesla har ikke annonceret V2G til nuværende modeller'] },
            ]}
          />
        </div>
      </ProseSection>

      <ProseSection id="ladebokse" heading="Hvilke ladebokse er V2G-klar?" alt>
        <p>En V2G-kompatibel bil er ikke nok – ladeboksen skal også understøtte tovejskommunikation. Hos enkel energi anbefaler vi disse fremtidssikrede løsninger:</p>
        <div className="grid sm:grid-cols-2 gap-4 my-6">
          <ChargerCard charger={ENKEL_PRODUCTS.chargers.zaptec_go_2} note="V2G-klar · billigste AC-løsning til hjemmet" />
          <ChargerCard charger={ENKEL_PRODUCTS.chargers.nexblue_edge_2} note="V2X-ready · prisvenligt alternativ" />
        </div>
        <p>
          Begge bokse er Saving Mode-kompatible og kommer med serviceaftale til 0 kr./md. Køber du en V2G-forberedt boks nu, er du klar den dag
          dansk lovgivning falder på plads – det koster ikke ekstra at fremtidssikre sig.
        </p>
      </ProseSection>

      <ProseSection id="okonomi" heading="Hvad kan du realistisk tjene?">
        <p>Det svenske pilotprojekt "Reverge" med 200 tovejsladere viser op til 19.000 kr. i besparelse om året under de mest gunstige forhold. Et mere realistisk dansk regnestykke ser sådan ud:</p>
        <div className="my-6">
          <FactGrid columns={3} facts={[
            { value: '20–40 kWh', label: 'Batteri til rådighed for V2G', sub: 'Resten reserveres til kørsel' },
            { value: '0,5–3,0 kr.', label: 'Prisforskel nat vs. spids', sub: 'pr. kWh i Danmark' },
            { value: '250–300', label: 'Dage bilen er tilsluttet', sub: 'for mange pendlere' },
          ]} />
        </div>
        <ProsCons
          pros={['Bilen tjener penge, mens den holder stille', 'Udnytter prisforskelle på spotmarkedet', 'Stabiliserer elnettet og fremmer grøn omstilling', 'Zaptec Go 2 er allerede klar – fremtidssikring koster ikke ekstra']}
          cons={['Endnu ingen officielle danske V2G-aftaler (ventes 2026–2027)', 'Kræver specifik bil + tovejsboks', 'Marginalt ekstra batterislid over tid', 'Kræver 3-faset installation']}
        />
        <p className="mt-4 text-sm text-gray-500">⚠️ Tallene er estimater baseret på svenske og tyske pilotprojekter. Det faktiske danske potentiale kendes ikke præcist endnu.</p>
      </ProseSection>

      <ProseSection id="krav" heading="Hvad kræver V2G af din installation?" alt>
        <p>For at kunne bruge V2G skal fire ting typisk være på plads:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>En <strong>V2G-kompatibel elbil</strong> – se tabellen ovenfor</li>
          <li>En <strong>godkendt tovejsladeboks</strong> – fx Zaptec Go 2</li>
          <li>En <strong>smart elmåler</strong> – de fleste danske husstande fik denne i 2020–2023</li>
          <li>En <strong>V2G-aftale med et elselskab</strong> – ventes i Danmark 2026–2027</li>
        </ul>
        <p className="mt-4">Din installation skal typisk have 3-faset tilslutning. De fleste nyere danske huse er klar; ældre huse med 25A enkeltfase kan kræve en opgradering.</p>
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål om V2G</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/sammenlign/ladeboks-v2g-ready', label: 'Ladeboks med V2G' },
        { path: '/zaptec-go-2', label: 'Zaptec Go 2' },
        { path: '/nexblue-edge-2', label: 'NexBlue Edge 2' },
        { path: '/portal/smart-charge', label: 'Saving Mode' },
        { path: '/opladere', label: 'Se alle ladebokse' },
        { path: '/elaftale', label: 'Se el-aftaler' },
      ]} />

      <RichCTA headline="Fremtidssikr din opladning i dag" subtext="Zaptec Go 2 er klar til V2G, når lovgivningen er det. Inkl. Saving Mode og serviceaftale til 0 kr./md." primary={{ path: '/zaptec-go-2', text: 'Se Zaptec Go 2' }} secondary={{ path: '/opladere', text: 'Alle ladebokse' }} />
      <SEODisclaimer page={page} />
    </article>
  );
}